import { useEffect, useRef, useState } from "react";
import PetInformation from "../PetInformation";

type StructureKey =
  | "liver"
  | "portalVein"
  | "gallbladder"
  | "spleen"
  | "pancreas"
  | "stomach"
  | "smallIntestines"
  | "colon"
  | "kidneys"
  | "adrenalGlands"
  | "urinaryBladder"
  | "testes"
  | "prostateGland"
  | "lymphNodes"
  | "peritoneum"
  | "other";

type StructureRow = {
  key: StructureKey;
  label: string;
  status: "" | "N" | "A";
  findings: string;
};

type FormState = {
  patientId: string;
  gender: string;
  veterinaryReferrer: string;
  clinicalHistory: string;
  examinationRequested: string;
  structures: StructureRow[];
  differentialDiagnosis: string;
  recommendedTests: string;
  interventionPerformedBy: string;
  fnaOrBiopsy: "" | "yes" | "no";
  fnaSite: string;
  cystocentesis: "" | "yes" | "no";
  abdominocentesis: "" | "yes" | "no";
  doctorName: string;
  examDate: string;
};

const initialStructureRows: StructureRow[] = [
  {
    key: "liver",
    label: "Liver",
    status: "",
    findings:
      "The liver is regular in size with a smooth, homogenous echo pattern throughout the parenchyma. No focal lesion or intrahepatic duct dilatation evident.",
  },
  {
    key: "portalVein",
    label: "Portal Vein",
    status: "",
    findings:
      "The portal vein is patent, demonstrates hepatopetal flow with a velocity of ___ cm/s. Hepatic veins demonstrate a normal calibre and regular configuration.",
  },
  {
    key: "gallbladder",
    label: "Gallbladder",
    status: "",
    findings:
      "Normal distended gallbladder with a uniform thin wall. Sludge / Choleliths seen within the dependent portion of the gallbladder. The common duct measures ___ mm at the level of the main portal branch.",
  },
  {
    key: "spleen",
    label: "Spleen",
    status: "",
    findings:
      "Regular sized and shaped spleen with a normal homogenous parenchymal echo pattern. No evidence of splenic disease.",
  },
  {
    key: "pancreas",
    label: "Pancreas",
    status: "",
    findings:
      "Where visualised, the pancreas is normal in size and echotexture with no evidence of pancreatic disease.",
  },
  {
    key: "stomach",
    label: "Stomach",
    status: "",
    findings:
      "The stomach is empty at time of scan. Where seen it has a normal wall layer architecture and gastric wall thickness (___ mm). No obvious foreign body seen within the contracted rugal folds. The pylorus and gastric outflow tract are identified and are normal on ultrasound.",
  },
  {
    key: "smallIntestines",
    label: "Small Intestines",
    status: "",
    findings:
      "The duodenum demonstrates a normal overall wall thickness (___ mm) and characteristic wall layer echo pattern. The remaining small intestines are also normal with normal wall layer architecture and wall thickness. There is no evidence of a foreign body, focal or diffuse disease within the small intestines.",
  },
  {
    key: "colon",
    label: "Colon",
    status: "",
    findings:
      "The colon contains air / faecal content / fluid at time of scan. The colonic wall layering and overall wall thickness, where seen, are unremarkable. The ICCJ is identified and is also normal.",
  },
  {
    key: "kidneys",
    label: "Kidneys",
    status: "",
    findings:
      "Both kidneys are normal in size, shape, and echogenicity, demonstrating cortico-medullary differentiation. There is no evidence of hydronephrosis or mass lesion. The left kidney measures ___ mm and the right measures ___ mm in long axis.",
  },
  {
    key: "adrenalGlands",
    label: "Adrenal Glands",
    status: "",
    findings:
      "Both adrenal glands are normal in size and echogenicity, demonstrating a normal characteristic shape. The left adrenal gland measures ___ mm and the right measures ___ mm at the caudal pole.",
  },
  {
    key: "urinaryBladder",
    label: "Urinary Bladder",
    status: "",
    findings:
      "The bladder is moderately filled at time of scan. The bladder wall is smooth, uniform and demonstrates a wall thickness within normal limits for fill status (___ mm).",
  },
  {
    key: "testes",
    label: "Testes",
    status: "",
    findings:
      "Neutered / Intact status noted. Both testes are correctly sited and appear normal in shape, size and echotexture.",
  },
  {
    key: "prostateGland",
    label: "Prostate Gland",
    status: "",
    findings:
      "The prostate gland is normal in size, shape and outline with normal echotexture of the gland.",
  },
  {
    key: "lymphNodes",
    label: "Lymph Nodes",
    status: "",
    findings:
      "Both the left medial iliac lymph node (LMILN) and right medial iliac lymph node (RMILN) are identified and are normal in size, shape and echotexture. No gross lymphadenopathy evident.",
  },
  {
    key: "peritoneum",
    label: "Peritoneum",
    status: "",
    findings: "No free abdominal fluid demonstrated.",
  },
  {
    key: "other",
    label: "Other",
    status: "",
    findings: "",
  },
];

const USGAbdomenMaleTemplate = () => {
  const [formData, setFormData] = useState<FormState>({
    patientId: "",
    gender: "",
    veterinaryReferrer: "",
    clinicalHistory: "",
    examinationRequested: "Full abdominal ultrasound survey",
    structures: initialStructureRows,
    differentialDiagnosis: "",
    recommendedTests: "",
    interventionPerformedBy: "",
    fnaOrBiopsy: "",
    fnaSite: "",
    cystocentesis: "",
    abdominocentesis: "",
    doctorName: "",
    examDate: "",
  });

  const textareaRefs = useRef<Array<HTMLTextAreaElement | null>>([]);
  const clinicalHistoryRef = useRef<HTMLTextAreaElement | null>(null);
  const differentialRef = useRef<HTMLTextAreaElement | null>(null);
  const recommendedTestsRef = useRef<HTMLTextAreaElement | null>(null);

  const autoResize = (ref: HTMLTextAreaElement | null) => {
    if (!ref) return;
    ref.style.height = "auto";
    ref.style.height = `${ref.scrollHeight}px`;
  };

  useEffect(() => {
    autoResize(clinicalHistoryRef.current);
    autoResize(differentialRef.current);
    autoResize(recommendedTestsRef.current);
    textareaRefs.current.forEach((ref) => autoResize(ref));
  }, [formData]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateStructure = (index: number, field: keyof StructureRow, value: string) => {
    setFormData((prev) => {
      const next = [...prev.structures];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, structures: next };
    });
  };

  const renderYesNo = (
    name: string,
    value: "" | "yes" | "no",
    onChange: (next: "" | "yes" | "no") => void,
  ) => {
    return (
      <div className="flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name={name}
            checked={value === "yes"}
            onChange={() => onChange("yes")}
            className="accent-black"
          />
          <span>Yes</span>
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            name={name}
            checked={value === "no"}
            onChange={() => onChange("no")}
            className="accent-black"
          />
          <span>No</span>
        </label>
      </div>
    );
  };

  return (
    <div className="w-full text-xs">
      <PetInformation />

      <div className="mb-3">
        <p className="font-semibold mb-1">Clinical History</p>
        <textarea
          ref={clinicalHistoryRef}
          value={formData.clinicalHistory}
          onChange={(e) => setField("clinicalHistory", e.target.value)}
          className="w-full border p-2 resize-none print:hidden"
          placeholder="Add clinical history..."
        />
        <div className="hidden print:block border p-2 whitespace-pre-wrap">{formData.clinicalHistory}</div>
      </div>

      <div className="mb-3">
        <p className="font-semibold mb-1">Examination Requested</p>
        <input
          type="text"
          className="border p-1 w-full"
          value={formData.examinationRequested}
          onChange={(e) => setField("examinationRequested", e.target.value)}
        />
      </div>

      <h3 className="text-sm font-semibold mb-2">Ultrasound Findings</h3>
      <table className="w-full border border-gray-300 border-collapse">
        <thead>
          <tr>
            <th className="border border-gray-300 p-2 text-left w-[18%]">Structure</th>
            <th className="border border-gray-300 p-2 text-left w-[16%]">Normal / Abnormal</th>
            <th className="border border-gray-300 p-2 text-left">Ultrasound Findings</th>
          </tr>
        </thead>
        <tbody>
          {formData.structures.map((row, idx) => (
            <tr key={row.key}>
              <td className="border border-gray-300 p-2 font-semibold align-top">{row.label}</td>
              <td className="border border-gray-300 p-2 align-top">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`${row.key}_status`}
                      checked={row.status === "N"}
                      onChange={() => updateStructure(idx, "status", "N")}
                      className="accent-black"
                    />
                    <span>N</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`${row.key}_status`}
                      checked={row.status === "A"}
                      onChange={() => updateStructure(idx, "status", "A")}
                      className="accent-black"
                    />
                    <span>A</span>
                  </label>
                </div>
              </td>
              <td className="border border-gray-300 p-2">
                <textarea
                  ref={(el) => {
                    textareaRefs.current[idx] = el;
                  }}
                  value={row.findings}
                  onChange={(e) => updateStructure(idx, "findings", e.target.value)}
                  className="w-full border p-1 resize-none print:hidden"
                />
                <div className="hidden print:block whitespace-pre-wrap">{row.findings}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 border border-gray-300 p-2">
        <p className="font-semibold mb-2">Ultrasound Guided Intervention</p>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold w-[140px]">Performed by</span>
            <input
              type="text"
              className="border p-1 flex-1"
              value={formData.interventionPerformedBy}
              onChange={(e) => setField("interventionPerformedBy", e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold w-[140px]">FNA / Biopsy</span>
            {renderYesNo("fnaOrBiopsy", formData.fnaOrBiopsy, (next) => setField("fnaOrBiopsy", next))}
            <span className="font-semibold">Site</span>
            <input
              type="text"
              className="border p-1 min-w-[220px] flex-1"
              value={formData.fnaSite}
              onChange={(e) => setField("fnaSite", e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold w-[140px]">Cystocentesis</span>
            {renderYesNo("cystocentesis", formData.cystocentesis, (next) => setField("cystocentesis", next))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-semibold w-[140px]">Abdominocentesis</span>
            {renderYesNo("abdominocentesis", formData.abdominocentesis, (next) =>
              setField("abdominocentesis", next),
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <p className="font-semibold mb-1">General Impression</p>
        <textarea
          ref={differentialRef}
          value={formData.differentialDiagnosis}
          onChange={(e) => setField("differentialDiagnosis", e.target.value)}
          className="w-full border p-2 resize-none print:hidden"
        />
        <div className="hidden print:block border p-2 whitespace-pre-wrap">{formData.differentialDiagnosis}</div>
      </div>

      <div className="mt-3">
        <p className="font-semibold mb-1">Recommended Tests</p>
        <textarea
          ref={recommendedTestsRef}
          value={formData.recommendedTests}
          onChange={(e) => setField("recommendedTests", e.target.value)}
          className="w-full border p-2 resize-none print:hidden"
        />
        <div className="hidden print:block border p-2 whitespace-pre-wrap">{formData.recommendedTests}</div>
      </div>

      <div className="mt-3 space-y-2">
        <div className="mt-4">
          <p className="text-sm font-semibold mb-2">Examiner Details</p>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold w-[140px]">Doctor Name</p>
              <input
                type="text"
                value={formData.doctorName}
                onChange={(e) => setField("doctorName", e.target.value)}
                className="border p-1 w-[240px] text-xs"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold w-[140px]">Date</p>
              <input
                type="date"
                value={formData.examDate}
                onChange={(e) => setField("examDate", e.target.value)}
                className="border p-1 w-[240px] text-xs print:hidden"
              />
              <span className="hidden print:inline text-xs">{formData.examDate}</span>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @media print {
            input, textarea {
              border: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default USGAbdomenMaleTemplate;
